import { createAction, createSlice } from '@reduxjs/toolkit';

import { OAuthClient, Platform } from '@mfe/shared/schema-types';

export enum Issuer {
  MV = 'MyViasat',
  Salesforce = 'Salesforce',
}

export interface RegistrationConfig {
  client: OAuthClient;
  issuer: string;
}

export interface AuthConfig {
  baseUrl: string;
  client: OAuthClient;
  myViasatUrl?: string;
  authMethod: 'web' | 'mobile';
}

export const initialClient = {
  clientId: '',
  redirectUrl: '',
};

interface FeatureFlags {
  showChangePlan: boolean;
  inMaintenance: boolean | null;
  getMockTier: boolean;
  enableFiber: boolean;
  disconnectRegions: Array<string>;
  sendSurveyToSalesforce: boolean;
  showEasyCareDisclaimers: boolean;
  enableBridgeMode: boolean;
  isPreauthZeroEnabled: boolean;
  unrestrictedRemovalProducts: string[];
  restrictAddonRemoval: boolean;
  enableUnleashedMaxTier: boolean;
}

export interface ConfigState extends FeatureFlags {
  loadingConfig: boolean;
  analyticsUrl: string;
  aemBaseUrl: string;
  aemContentBaseUrl: string;
  auth: AuthConfig;
  env: string;
  isProfileReadOnly: boolean;
  isPWA: boolean;
  issuer: Issuer;
  mfeEnv: string;
  platform: Platform;
  reg: RegistrationConfig;
  version: string;
  vppUrl: string;
}

export const initialConfigState: ConfigState = {
  loadingConfig: false,
  auth: {
    baseUrl: '',
    client: initialClient,
    authMethod: 'web',
    myViasatUrl: '',
  },
  aemBaseUrl: '',
  aemContentBaseUrl: '',
  analyticsUrl: '',
  env: '',
  isProfileReadOnly: false,
  isPWA: false,
  issuer: Issuer.MV,
  mfeEnv: '',
  platform: Platform.Web,
  reg: { client: initialClient, issuer: '' },
  showChangePlan: true,
  version: '4.4.3',
  vppUrl: '',
  inMaintenance: null,
  getMockTier: false,
  disconnectRegions: [],
  sendSurveyToSalesforce: false,
  showEasyCareDisclaimers: false,
  isPreauthZeroEnabled: false,
  enableBridgeMode: false,
  enableFiber: false,
  unrestrictedRemovalProducts: [],
  restrictAddonRemoval: false,
  enableUnleashedMaxTier: false,
};

export const configSlice = createSlice({
  name: 'config',
  initialState: initialConfigState,
  reducers: {
    fetchConfig: (state) => {
      state.loadingConfig = true;
    },

    setIssuer: (state, { payload }: { payload: Issuer }) => {
      state.issuer = payload;
    },
    setConfig: (state, { payload }: { payload: Partial<ConfigState> }) => {
      return { ...state, ...payload };
    },
    setEnvConfig: (state, { payload }: { payload: string }) => {
      state.env = payload;
    },
  },
});

export const { setConfig, setIssuer, fetchConfig } = configSlice.actions;

export const selectConfig = (state: { config: ConfigState }) => state.config;
export const getMaintenanceStatus = createAction('config/getMaintenanceStatus');
